import { Dialog as SheetPrimitive } from "radix-ui";
import { type ComponentProps, type ReactNode, useRef } from "react";
import { cn } from "./cn";
import { Icon } from "./icon";

// A panel that slides in from an edge (the navigation drawer on a narrow screen). It is a modal dialog: Radix traps focus, closes on
// Escape and returns focus to the control that opened it. It always needs a title (`SheetTitle`); hide it with `sr-only` if the design has none.
// A sheet that the address opens has no `SheetTrigger`, and Radix then drops the focus on close (it returns focus to the trigger only), so the
// content remembers the element that had focus when it opened and returns focus to it, when that element is still on the page. A sheet WITH a
// trigger is left to Radix, which returns the focus to the trigger (some browsers do not focus a button that is clicked, so the element that
// had focus at the time is not the trigger).

export const Sheet = SheetPrimitive.Root;
export const SheetTrigger = SheetPrimitive.Trigger;
export function SheetContent({
  className,
  children,
  side = "left",
  closeLabel = "Close menu",
  onOpenAutoFocus,
  onCloseAutoFocus,
  ...props
}: ComponentProps<typeof SheetPrimitive.Content> &
  Readonly<{ side?: "left" | "right"; closeLabel?: string; children: ReactNode }>) {
  const opener = useRef<HTMLElement | null>(null);
  const hasTrigger = useRef(false);
  return (
    <SheetPrimitive.Portal>
      <SheetPrimitive.Overlay className="fixed inset-0 z-[var(--lf-z-overlay)] bg-foreground/50" />
      <SheetPrimitive.Content
        className={cn(
          "fixed inset-y-0 z-[var(--lf-z-modal)] flex w-[min(20rem,calc(100vw-3rem))] flex-col bg-sidebar text-sidebar-foreground shadow-lg outline-hidden",
          side === "left" ? "left-0 border-r border-sidebar-border" : "right-0 border-l",
          className,
        )}
        onOpenAutoFocus={(event) => {
          const content = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
          hasTrigger.current =
            content !== null &&
            Array.from(document.querySelectorAll("[aria-controls]")).some(
              (element) => element.getAttribute("aria-controls") === content.id,
            );
          opener.current =
            document.activeElement instanceof HTMLElement ? document.activeElement : null;
          onOpenAutoFocus?.(event);
        }}
        onCloseAutoFocus={(event) => {
          onCloseAutoFocus?.(event);
          if (event.defaultPrevented || hasTrigger.current) return;
          event.preventDefault();
          const element = opener.current;
          if (element?.isConnected && element !== document.body) element.focus();
        }}
        {...props}
      >
        {children}
        <SheetPrimitive.Close
          aria-label={closeLabel}
          className="absolute top-2 right-2 inline-flex size-11 cursor-pointer items-center justify-center rounded-sm text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground compact:size-8"
        >
          <Icon name="cancel-01" className="size-5" />
        </SheetPrimitive.Close>
      </SheetPrimitive.Content>
    </SheetPrimitive.Portal>
  );
}

export function SheetTitle({ className, ...props }: ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title className={cn("m-0 text-base font-semibold", className)} {...props} />
  );
}

export function SheetDescription({
  className,
  ...props
}: ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      className={cn("m-0 text-sm text-muted-foreground", className)}
      {...props}
    />
  );
}
